import { CSSProperties } from "react";
import Cookies from "js-cookie";

export const generateUrl = (urlMode: string): string => {
  const envMode = process.env.REACT_APP_ENV;
  let backendUrlPreFix = "",
    externalUrl = "",
    interviewUrl = "",
    socketUrlPrefix = "";

  if (envMode === "prod") {
    interviewUrl = "meeting.";
    backendUrlPreFix = "api.";
    externalUrl = "https://prod-in2.100ms.live/hmsapi/incruiter-prod.app.100ms.live/";
  } else if (envMode === "test" || envMode === "preprod") {
    interviewUrl = "test.v1.interview.";
    socketUrlPrefix = "test-";
    backendUrlPreFix = "test.api.";
    externalUrl = "https://prod-in2.100ms.live/hmsapi/incruiter-preprod-bot-room.app.100ms.live/";
  } else if (envMode === "dev2") {
    interviewUrl = "dev.v2.interview.";
    socketUrlPrefix = "dev2-";
    backendUrlPreFix = "dev.v2.api.";
    externalUrl = "https://prod-in2.100ms.live/hmsapi/incruiter-preprod-bot-room.app.100ms.live/";
  } else {
    interviewUrl = "dev.v1.interview.";
    socketUrlPrefix = "dev-";
    backendUrlPreFix = "dev.api.";
    externalUrl = "https://prod-in2.100ms.live/hmsapi/incruiter-preprod-bot-room.app.100ms.live/";
  }

  switch (urlMode) {
    case "token_generation":
      return externalUrl;
    case "socket":
      return `https://${socketUrlPrefix}video-feedback-system.incruiter.com`;
    case "proctoring":
      return `https://${socketUrlPrefix}proctoring.incruiter.com`;
    case "interview_backend":
      return `https://${backendUrlPreFix}incruiter.com/interview-system/api/v1/incbot`;
    case "candidate_backend":
      return `https://${backendUrlPreFix}incruiter.com/candidate-listing-system/api/v1/incscreen`;
    case "interview_system":
      return `https://${backendUrlPreFix}incruiter.com/interview-system/api/v1`;
    case "video_backend":
      return `https://${backendUrlPreFix}incruiter.com/video-feedback-system/api/v1/video-platform`;
    case "meeting":
      return `https://${interviewUrl}incruiter.com`;
    case "user_backend":
      return `https://${backendUrlPreFix}incruiter.com/user-auth-system/api/v1`;
    case "conversational-ai":
      return `wss://${socketUrlPrefix}ai-services.incruiter.com/ws`;
    default:
      return "";
  }
};
const envMode = process.env.REACT_APP_ENV === "dev2" ? "dev" : process.env.REACT_APP_ENV;
export const getAccessToken = Cookies.get(`token_${envMode}`) ?? "";
interface IncScreenProfileForm {
  code: string;
  name: string;
  email: string;
  phone: string;
  /** [2026-09-17] ADDED — SEPARATE TICKET: who produced the feedback. See `withFeedbackSourceRow`. */
  endCallStatus?: string;
  /** [2026-09-17] ADDED — when the feedback was actually generated. */
  interviewEndDate?: string;
  interviewDate: string;
  position: string;
  company: string;
  status: string;
  duration: number;
  interviewType: string;
  recording: any[];
  interviewLanguage?: string;
}

export interface FeedbackStatusOption {
  value: string;
  label: string;
  bgColor: string;
  textColor: string;
}

interface ProfileForm {
  code: string;
  name: string;
  email: string;
  phone: string;
  /** [2026-09-17] ADDED — SEPARATE TICKET: who produced the feedback. See `withFeedbackSourceRow`. */
  endCallStatus?: string;
  /** [2026-09-17] ADDED — when the feedback was actually generated. */
  interviewEndDate?: string;
  interviewDate: string;
  timeZone: string;
  timeOffset: string;
  position: string;
  company: string;
  status: string;
  finalRating: number;
  logo: string;
  candidateImage: string;
  interviewDuration: number;
  ratingSystem: {
    low: number;
    medium: number;
  };
  interviewType?: string;
  interviewLanguage?: string;
  feedbackStatus?: FeedbackStatusOption[];
}

interface IncScreenFeedbackForm {
  screeningQuestions: any[];
  questions: any[];
  availability: {
    value: string;
    benchmark: string;
    category?: string;
  };
  currentEmployment: {
    value: string;
    benchmark: string;
    category?: string;
  };
  salaryExpectation: {
    value: string;
    benchmark: string;
    category?: string;
  };
  workSetup: {
    value: string;
    benchmark: string;
    category?: string;
  };
  motivationScore?: number;
  communicationAssessment?: {
    fluencyRating?: number;
    clarityRating?: number;
    grammarRating?: number;
    confidenceRating?: number;
  };
  strengths?: any[];
  dealBreakers?: any[];
  /** Summary bullets from remarks (Overall Conclusion), not raw chat lines */
  transcripts: any[];
  /** Raw `feedback.transcript` turns (person + text); use for e.g. "Start Chat" detection */
  conversationTranscript?: { person?: string; text?: string }[];

  profileFitmentScore?: number;
  profileFitmentStrengths?: any[];
  positionSpecificRemarks?: any[];
}

interface FeedbackForm {
  isSkillBased?: boolean;
  feedbackTemplate?: boolean;
  duration: number;
  totalDuration: number;
  codingStats: any;
  positionStats: any;
  aptitudeStats: any;
  aptitudeSectionData?: { sectionsData: any[]; totalAptitudeTime: number };
  codingAndAptitude?: boolean;
  codingFinalRemark?: any[];
  finalPercentile: number;
  psychometricOverallScore?: number | null;
  psychometricPercentile?: number | null;
  totalTimeTaken: number;
  totalParticipants: number;
  version: number;
  resumeSummary: string;
  questions: {
    status: boolean;
    data: any[];
  };
  proctoringLogs: {
    status: boolean;
    messages: any[];
    screenshots: any[];
    externalDevices: any | null;
  };
  screenshots: {
    status: boolean;
    images: any[];
  };
  deviceScreenshots: {
    status: boolean;
    images: any[];
  };
  finalRemarks: {
    status: boolean;
    data: string;
  };
  score: number | { totalMarks?: number; scoredMarks?: number; percentage?: number } | null;
  overallFeedback: {
    title: string;
    value: number;
  }[];
  softSkillAssessment: {
    title: string;
    value: number;
    status: boolean;
  }[];
  interviewAssessment: {
    title: string;
    value: number;
  }[];
  /** Ordered psychometric dimensions; populated from interviewAssessment when present */
  psychometricSkillsScores?: {
    title: string;
    value: number;
  }[];
  /** Skill-wise psychometric report from API (`feedback.psychometricSkillWise`) */
  psychometricSkillWise?: {
    position?: string;
    skill_results?: Array<{
      skill: string;
      score: number;
      comment: string;
      sub_skills?: Array<{ sub_skill: string; score: number; comment: string }>;
    }>;
  } | null;
  psychometricOverallReport?: {
    position?: string;
    strengths?: string[];
    weaknesses?: string[];
    areas_of_progress?: string[];
    conclusion?: string;
  } | null;
  /** Answered psychometric Q&A from `feedback.psychometricQuestions` */
  psychometricQuestionsFeedback?: Array<{
    questionType?: string;
    question?: string;
    role?: string;
    candidate_answer?: string;
    skill?: string;
    sub_skill?: string;
    answerAudioUrl?: string | null;
    transcript?: string | null;
    submittedAt?: string;
  }>;
  /** `interview.psychometricQuestions` — option text for MCQ-style rows */
  psychometricQuestionBank?: Array<{
    question?: string;
    type?: string;
    options?: Record<string, string>;
    skill?: string;
    sub_skill?: string;
  }>;
  AIGenTextBanner: boolean;
  AIGenText?: boolean;
  userSettings: any;
  cefrScores?: {
    reading: {
      rawScore: string;
      percentageScore: number;
      finalCefrLevel: string;
      rawAssessment: string;
      percentageAssessment: string;
      levelAssessment: string;
    };
    listening: {
      rawScore: string;
      percentageScore: number;
      finalCefrLevel: string;
      rawAssessment: string;
      percentageAssessment: string;
      levelAssessment: string;
    };
    writing: {
      vocabulary: string;
      coherence: string;
      grammar: string;
      overallCefr: string;
      vocabularyAssessment: string;
      coherenceAssessment: string;
      grammarAssessment: string;
      overallAssessment: string;
    };
    speaking: {
      pronunciation: string;
      fluency: string;
      grammar: string;
      overallCefr: string;
      pronunciationAssessment: string;
      fluencyAssessment: string;
      grammarAssessment: string;
      overallAssessment: string;
    };
  };
  cefrScreeningSections?: {
    id: string;
    title: string;
    description: string;
    defaultExpanded?: boolean;
    paragraph?: {
      title?: string;
      intro?: string;
      content?: string;
      paragraphs?: string[];
    };
    writingPrompt?: string;
    userInput?: string;
    speakingTopic?: string;
    recordingUrl?: string;
    audioItems?: {
      id: string;
      label: string;
      audioUrl?: string;
      duration?: string;
      questions: {
        number: string;
        text: string;
        options?: string[];
        userAnswer: string;
        correctAnswer: string;
        isCorrect: boolean;
      }[];
    }[];
    questions?: {
      number: string;
      text: string;
      options?: string[];
      userAnswer: string;
      correctAnswer: string;
      isCorrect: boolean;
    }[];
  }[];
}

interface Permissions {
  showProctoringLogs: boolean;
  showPercentileRank: boolean;
  softSkills: Record<string, boolean>;
  finalRemarks: Record<string, boolean>;
}

interface IncbotFeedbackDataResult {
  profile: ProfileForm;
  feedback: FeedbackForm;
  permissions: Permissions;
  psychometricSkills?: any;
}

interface IncScreenFeedbackDataResult {
  profile: IncScreenProfileForm;
  feedback: IncScreenFeedbackForm;
}

export const incScreenFeedbackData = (interview: any): IncScreenFeedbackDataResult => {
  const profile = {
    name: interview?.candidate?.name,
    status: ["Disqualified", "Qualified", "Completed"].includes(interview?.status)
      ? interview?.status
      : interview?.candidateStatus?.toLowerCase() === "rejected"
        ? "Disqualified"
        : "Qualified",
    email: interview?.candidate?.email,
    phone: `+${interview?.candidate?.phone}`,
    code: interview?.code,
    position: interview?.position?.title,
    company: interview?.employer?.name ?? "InCruiter",
    // [2026-09-17] ADDED — SEPARATE TICKET: carried through so ProctoringLogsView can name
    // who produced the feedback. Already in the API payload; nothing new is fetched.
    endCallStatus: interview?.endCallStatus,
    // The real server time the interview was closed — NOT the back-dated end ROW.
    interviewEndDate: interview?.interviewEndDate,
    interviewDate: interview?.interviewStartDate,
    duration: interview?.interviewDuration || 0,
    interviewType: interview?.interviewType,
    recording: interview.recording,
    interviewLanguage: interview?.interviewLanguage,
  };

  const transcript = interview?.feedback?.transcript || [];

  const finalQuestionList = [];

  for (let i = 0; i < transcript.length; i++) {
    const current = transcript[i];
    if (current.person === "agent") {
      const next = transcript[i + 1];
      // [2026-08-19] CHANGED — a trailing agent turn with nothing after it fell through the
      // `if (next)` guard and was silently discarded, so the agent's closing line never rendered
      // whenever the candidate stayed silent or hung up straight after it. That is the reported
      // "sometimes the closing is missing from the transcript, but it is on the Vapi recording".
      // It now renders on its own with answer: "" — ConversationItem (Feedback.tsx:81) already
      // guards on `answer`, so an empty answer draws the agent bubble alone. Every other input
      // produces byte-identical output to the old block. Consumer is only the Conversation block
      // at Feedback.tsx:1157 (via feedback.questions); screeningQuestions/scores are untouched.
      // --- OLD (pre-2026-08-19) ---
      // if (next) {
      //   if (next.person !== "agent") {
      //     finalQuestionList.push({
      //       question: current.text,
      //       answer: next.text,
      //     });
      //     i++;
      //   } else {
      //     finalQuestionList.push({
      //       question: current.text,
      //       answer: "",
      //     });
      //   }
      // }
      // --- END OLD ---
      if (next && next.person !== "agent") {
        finalQuestionList.push({
          question: current.text,
          answer: next.text,
        });
        i++;
      } else {
        finalQuestionList.push({
          question: current.text,
          answer: "",
        });
      }
    }
  }

  const interviewAssessment = interview?.feedback?.interviewAssessment;
  const remark = interview?.feedback?.remark;
  const screeningQuestions = interview.feedback?.questions;

  // Feedback Object
  const feedback = {
    questions: finalQuestionList,
    availability: interviewAssessment?.availability,
    currentEmployment: interviewAssessment?.employment,
    salaryExpectation: interviewAssessment?.salary,
    workSetup: interviewAssessment?.workSetup,
    motivationScore: remark?.[0]?.rating,
    communicationAssessment: interview?.feedback?.communicationAssessment,
    strengths: remark?.[0]?.value,
    dealBreakers: remark?.[1]?.value,
    transcripts: remark?.[2]?.value,
    conversationTranscript: transcript,
    screeningQuestions,
    version: interview?.version,

    profileFitmentScore: remark?.[3]?.rating,
    profileFitmentStrengths: remark?.[3]?.value,
    positionSpecificRemarks: remark?.[4]?.value,
    logs: interview?.logs ?? [],
  };

  return {
    profile,
    feedback,
  };
};

/** Appends containerSasToken as query param to a media URL */
export const withSas = (url: string | undefined | null, sas: string | undefined | null): string => {
  if (!url) return "";
  if (!sas) return url;
  return url.includes("?") ? `${url}&${sas}` : `${url}?${sas}`;
};

/** Maps API remark to short assessment label */
const mapRemarkToAssessment = (remark: string): string =>
  remark && remark.length > 0 ? remark : "—";

/** Maps a CEFR level string to a numeric score 0-100 for averaging */
const cefrLevelToNumeric = (level: string): number => {
  const map: Record<string, number> = {
    A0: 0,
    A1: 15,
    A2: 30,
    "B1-": 40,
    B1: 50,
    "B1+": 55,
    "B2-": 60,
    B2: 70,
    "B2+": 75,
    "C1-": 80,
    C1: 85,
    "C1+": 90,
    "C2-": 95,
    C2: 100,
  };
  return map[level?.replace(/\s/g, "")] ?? 0;
};

/** Returns base FeedbackForm for CEFR (used when API has no data) */
const getCefrBaseFeedback = (): FeedbackForm => ({
  isSkillBased: false,
  feedbackTemplate: true,
  duration: 0,
  totalDuration: 0,
  codingStats: null,
  positionStats: null,
  aptitudeStats: null,
  aptitudeSectionData: { sectionsData: [], totalAptitudeTime: 0 },
  finalPercentile: 0,
  totalTimeTaken: 0,
  totalParticipants: 0,
  version: 1,
  resumeSummary: "CEFR language assessment.",
  questions: { status: false, data: [] },
  proctoringLogs: {
    status: false,
    messages: [],
    screenshots: [],
    externalDevices: null,
  },
  screenshots: { status: false, images: [] },
  deviceScreenshots: { status: false, images: [] },
  finalRemarks: { status: false, data: "" },
  score: { totalMarks: 100, scoredMarks: 0, percentage: 0 },
  overallFeedback: [
    { title: "CEFR Level", value: 0 },
    { title: "Resume Rating", value: 0.7 },
    { title: "Suspicious Activity", value: 0 },
  ],
  softSkillAssessment: [],
  interviewAssessment: [],
  psychometricSkillsScores: [],
  psychometricSkillWise: null,
  psychometricOverallReport: null,
  psychometricQuestionsFeedback: [],
  psychometricQuestionBank: [],
  AIGenTextBanner: false,
  AIGenText: false,
  userSettings: null,
  codingAndAptitude: false,
  codingFinalRemark: [],
  cefrScores: {
    reading: {
      rawScore: "0/0",
      percentageScore: 0,
      finalCefrLevel: "—",
      rawAssessment: "—",
      percentageAssessment: "—",
      levelAssessment: "—",
    },
    listening: {
      rawScore: "0/0",
      percentageScore: 0,
      finalCefrLevel: "—",
      rawAssessment: "—",
      percentageAssessment: "—",
      levelAssessment: "—",
    },
    writing: {
      vocabulary: "—",
      coherence: "—",
      grammar: "—",
      overallCefr: "—",
      vocabularyAssessment: "—",
      coherenceAssessment: "—",
      grammarAssessment: "—",
      overallAssessment: "—",
    },
    speaking: {
      pronunciation: "—",
      fluency: "—",
      grammar: "—",
      overallCefr: "—",
      pronunciationAssessment: "—",
      fluencyAssessment: "—",
      grammarAssessment: "—",
      overallAssessment: "—",
    },
  },
  cefrScreeningSections: [],
});

/** Transforms CEFR API feedback format to FeedbackForm format */
const transformCefrApiFeedbackToForm = (apiFeedback: any, interview?: any): FeedbackForm => {
  const base = getCefrBaseFeedback();
  const sas = interview?.containerSasToken ?? null;

  const readingSummary = apiFeedback?.readingSummary;
  const listeningSummary = apiFeedback?.listeningSummary;
  const writingSummary = apiFeedback?.writingSummary;
  const speakingSummary = apiFeedback?.speakingSummary;
  const overallCefr = apiFeedback?.overallCefr ?? null;
  const writingData = apiFeedback?.writing ?? [];
  const speakingData = apiFeedback?.speaking ?? [];

  // Use cefrQuestions from feedback (answered), filtered by activeStatus from interview.cefrQuestions
  const feedbackCefrQuestions = apiFeedback?.cefrQuestions ?? [];
  const interviewCefrQuestions: any[] = interview?.cefrQuestions ?? [];

  // Build a set of active section names from interview.cefrQuestions
  // A section is active if at least one question in that section has activeStatus !== false
  const activeSections = new Set<string>();
  if (interviewCefrQuestions.length > 0) {
    interviewCefrQuestions.forEach((q: any) => {
      if (q.activeStatus !== false && q.section) activeSections.add(q.section);
    });
  } else {
    // fallback: treat all sections in feedback as active
    feedbackCefrQuestions.forEach((q: any) => {
      if (q.section) activeSections.add(q.section);
    });
  }

  // Resume summary from interview feedback resumeSummary, fallback to building from CEFR levels
  const rawResumeSummary = interview?.feedback?.resumeSummary ?? apiFeedback?.resumeSummary ?? "";
  const resumeSummary =
    rawResumeSummary ||
    [
      "CEFR language assessment.",
      listeningSummary?.cefrLevel ? `- Listening: ${listeningSummary.cefrLevel}` : null,
      readingSummary?.cefrLevel ? `- Reading: ${readingSummary.cefrLevel}` : null,
      writingSummary?.cefrLevel ? `- Writing: ${writingSummary.cefrLevel}` : null,
      speakingSummary?.cefrLevel ? `- Speaking: ${speakingSummary.cefrLevel}` : null,
    ]
      .filter(Boolean)
      .join("\n");

  // Build finalRemarks from API summaries and remarks
  const finalRemarksParts: string[] = [];
  if (readingSummary?.remark && activeSections.has("Reading")) {
    finalRemarksParts.push(`Reading:\n- ${readingSummary.remark}`);
  }
  if (listeningSummary?.remark && activeSections.has("Listening")) {
    finalRemarksParts.push(`Listening:\n- ${listeningSummary.remark}`);
  }
  if (writingSummary?.remark && activeSections.has("Writing")) {
    finalRemarksParts.push(`Writing:\n- ${writingSummary.remark}`);
  }
  if (speakingSummary?.remark && activeSections.has("Speaking")) {
    finalRemarksParts.push(`Speaking:\n- ${speakingSummary.remark}`);
  }

  // cefrScores from API
  const readingScores = {
    rawScore: readingSummary?.rawScore ?? "0/0",
    percentageScore: readingSummary?.percentage ?? 0,
    finalCefrLevel: readingSummary?.cefrLevel ?? "—",
    rawAssessment: mapRemarkToAssessment(readingSummary?.remark),
    percentageAssessment: mapRemarkToAssessment(readingSummary?.remark),
    levelAssessment: mapRemarkToAssessment(readingSummary?.remark),
  };

  const listeningScores = {
    rawScore: listeningSummary?.rawScore ?? "0/0",
    percentageScore: listeningSummary?.percentage ?? 0,
    finalCefrLevel: listeningSummary?.cefrLevel ?? "—",
    rawAssessment: mapRemarkToAssessment(listeningSummary?.remark),
    percentageAssessment: mapRemarkToAssessment(listeningSummary?.remark),
    levelAssessment: mapRemarkToAssessment(listeningSummary?.remark),
  };

  const writingScores = {
    vocabulary:
      writingSummary?.breakdown?.vocab?.cefr ?? writingData[0]?.cefrScores?.vocabulary ?? "—",
    coherence:
      writingSummary?.breakdown?.coherence?.cefr ?? writingData[0]?.cefrScores?.coherence ?? "—",
    grammar: writingSummary?.breakdown?.grammar?.cefr ?? writingData[0]?.cefrScores?.grammar ?? "—",
    overallCefr: writingSummary?.cefrLevel ?? writingData[0]?.cefrScores?.overall ?? "—",
    vocabularyAssessment: mapRemarkToAssessment(writingSummary?.remark),
    coherenceAssessment: mapRemarkToAssessment(writingSummary?.remark),
    grammarAssessment: mapRemarkToAssessment(writingSummary?.remark),
    overallAssessment: mapRemarkToAssessment(writingSummary?.remark),
  };

  const speakingScores = {
    pronunciation: speakingSummary?.breakdown?.pronunciation?.cefr ?? "—",
    fluency: speakingSummary?.breakdown?.fluency?.cefr ?? "—",
    grammar: speakingSummary?.breakdown?.grammar?.cefr ?? "—",
    overallCefr: speakingSummary?.cefrLevel ?? "—",
    pronunciationAssessment: mapRemarkToAssessment(speakingSummary?.remark),
    fluencyAssessment: mapRemarkToAssessment(speakingSummary?.remark),
    grammarAssessment: mapRemarkToAssessment(speakingSummary?.remark),
    overallAssessment: mapRemarkToAssessment(speakingSummary?.remark),
  };

  // Build cefrScreeningSections from cefrQuestions (only active sections)
  const sections: any[] = [];
  const listeningItems = feedbackCefrQuestions.filter(
    (q: any) => q.section === "Listening" && activeSections.has("Listening")
  );
  const readingItems = feedbackCefrQuestions.filter(
    (q: any) => q.section === "Reading" && activeSections.has("Reading")
  );
  const writingItems = feedbackCefrQuestions.filter(
    (q: any) => q.section === "Writing" && activeSections.has("Writing")
  );
  const speakingItems = feedbackCefrQuestions.filter(
    (q: any) => q.section === "Speaking" && activeSections.has("Speaking")
  );

  if (listeningItems.length > 0) {
    const audioItems = listeningItems.map((item: any, idx: number) => {
      const qs = (item.questions ?? []).map((q: any, qIdx: number) => ({
        number: `${idx + 1}.${qIdx + 1}`,
        text: q.questionText ?? q.question ?? "",
        options: q.options ?? [],
        userAnswer: q.answer ?? q.Answer ?? "",
        correctAnswer: q.correctAnswer ?? q.options?.[q.correctAnswerIndex] ?? "",
        isCorrect: q.isCorrect ?? false,
      }));
      const totalSec = Math.floor((item.timeToAnswer ?? 0) / 60);
      const totalMod = (item.timeToAnswer ?? 0) % 60;
      const duration = `${totalSec}:${String(totalMod).padStart(2, "0")}`;
      return {
        id: item._id ?? `audio${idx + 1}`,
        label: `Audio${idx + 1}`,
        duration,
        audioUrl: withSas(item.audioUrl, sas),
        questions: qs,
      };
    });
    sections.push({
      id: "listening",
      title: "Listening Section",
      description:
        "Overview of the candidate's audio comprehension, accent recognition, and ability to process spoken information.",
      audioItems,
    });
  }

  if (readingItems.length > 0) {
    const builtReadingItems = readingItems.map((item: any, idx: number) => {
      const qs = (item.questions ?? []).map((q: any, qIdx: number) => ({
        number: `${idx + 1}.${qIdx + 1}`,
        text: q.question ?? q.questionText ?? "",
        options: q.options ?? [],
        userAnswer: q.answer ?? "",
        correctAnswer: q.correctAnswer ?? q.options?.[q.correctAnswerIndex] ?? "",
        isCorrect: q.isCorrect ?? false,
      }));
      return {
        id: item._id ?? `reading${idx + 1}`,
        label: `Passage ${idx + 1}`,
        paragraph: item.passage
          ? { title: "Reading Passage", paragraphs: [item.passage] }
          : undefined,
        questions: qs,
      };
    });
    sections.push({
      id: "reading",
      title: "Reading Section",
      description:
        "Overview of the candidate's text analysis skills, vocabulary range, and interpretation of written passages.",
      readingItems: builtReadingItems,
    });
  }

  if (writingItems.length > 0) {
    const apiWritingResults = apiFeedback?.writing ?? [];
    const writingSections = writingItems.map((item: any) => {
      const match = apiWritingResults.find(
        (w: any) => w.taskId === item.task_id || w.taskId === item.taskId
      );
      return {
        prompt: item.prompt ?? match?.prompt ?? "",
        userInput: item.answer ?? match?.candidateAnswer ?? "",
      };
    });
    const section: any = {
      id: "writing",
      title: "Writing Section",
      description:
        "Overview of the candidate's structural coherence, grammatical accuracy, and written expression capabilities.",
    };
    if (writingSections.length === 1) {
      section.writingPrompt = writingSections[0].prompt;
      section.userInput = writingSections[0].userInput;
    } else {
      section.writingItems = writingSections;
    }
    sections.push(section);
  }

  if (speakingItems.length > 0) {
    const apiSpeakingResults = apiFeedback?.speaking ?? [];
    const speakingSections = speakingItems.map((item: any) => {
      const match = apiSpeakingResults.find(
        (s: any) => s.taskId === item.task_id || s.taskId === item.taskId
      );
      return {
        topic: item.task ?? match?.task ?? "",
        recordingUrl: withSas(match?.audioUrl ?? item.answer ?? "", sas),
      };
    });
    sections.push({
      id: "speaking",
      title: "Speaking Section",
      description:
        "Overview of the candidate's oral fluency, pronunciation, and ability to articulate thoughts spontaneously.",
      speakingItems: speakingSections.length > 0 ? speakingSections : undefined,
      speakingTopic: speakingSections[0]?.topic,
      recordingUrl: speakingSections[0]?.recordingUrl,
    });
  }

  sections.forEach((s) => {
    // s.defaultExpanded = false;
    s.defaultExpanded = sections.length === 1;
  });

  // Compute average CEFR level across all active sections
  const cefrLevelNumerics: number[] = [];
  if (activeSections.has("Reading") && readingSummary?.cefrLevel)
    cefrLevelNumerics.push(cefrLevelToNumeric(readingSummary.cefrLevel));
  if (activeSections.has("Listening") && listeningSummary?.cefrLevel)
    cefrLevelNumerics.push(cefrLevelToNumeric(listeningSummary.cefrLevel));
  if (activeSections.has("Writing") && writingSummary?.cefrLevel)
    cefrLevelNumerics.push(cefrLevelToNumeric(writingSummary.cefrLevel));
  if (activeSections.has("Speaking") && speakingSummary?.cefrLevel)
    cefrLevelNumerics.push(cefrLevelToNumeric(speakingSummary.cefrLevel));
  const avgCefrNumeric = cefrLevelNumerics.length
    ? cefrLevelNumerics.reduce((a, b) => a + b, 0) / cefrLevelNumerics.length
    : 0;
  const cefrLevelValue = avgCefrNumeric / 100;

  // Overall percentage: average of active section percentages
  const pctValues: number[] = [];
  if (activeSections.has("Reading") && readingSummary)
    pctValues.push(readingSummary.percentage ?? 0);
  if (activeSections.has("Listening") && listeningSummary)
    pctValues.push(listeningSummary.percentage ?? 0);
  if (activeSections.has("Writing") && writingSummary)
    pctValues.push(cefrLevelToNumeric(writingSummary.cefrLevel ?? "—"));
  if (activeSections.has("Speaking") && speakingSummary)
    pctValues.push(cefrLevelToNumeric(speakingSummary.cefrLevel ?? "—"));
  const avgPct = pctValues.length ? pctValues.reduce((a, b) => a + b, 0) / pctValues.length : 0;

  // Suspicious activity from proctoring messages
  const proctoringMessages = apiFeedback?.proctoring?.messages ?? [];
  const suspiciousActivity = (() => {
    if (
      interview?.feedback?.overAllRating &&
      "suspiciousActivity" in interview.feedback.overAllRating
    ) {
      return interview.feedback.overAllRating.suspiciousActivity || 0;
    }

    const switchedTab = proctoringMessages.some((m: any) =>
      m.text?.includes("Candidate switched to another tab")
    );
    const lookedAway = proctoringMessages.some((m: any) =>
      m.text?.includes("Looking away from screen")
    );
    const networkDisconnect = proctoringMessages.filter((m: any) => m.type === "red").length;
    if (switchedTab) return 10;
    if (lookedAway) return 5;
    if (networkDisconnect > 2) return 7;
    if (networkDisconnect > 0) return 3;
    return 0;
  })();

  // overallScore from API: value like 0.7 = 70 on 0-100 scale → used for Total row only
  const overallScoreRaw: number | null = apiFeedback?.overallScore ?? null;
  const overallScoreNumeric: number = overallScoreRaw !== null ? overallScoreRaw * 100 : 0;

  const numericToCefrLabel = (numeric: number): string => {
    const levels: [string, number][] = [
      ["A0", 0],
      ["A1", 15],
      ["A2", 30],
      ["B1-", 40],
      ["B1", 50],
      ["B1+", 55],
      ["B2-", 60],
      ["B2", 70],
      ["B2+", 75],
      ["C1-", 80],
      ["C1", 85],
      ["C1+", 90],
      ["C2-", 95],
      ["C2", 100],
    ];
    let closest = "A0",
      minDiff = Infinity;
    for (const [label, score] of levels) {
      const diff = Math.abs(score - numeric);
      if (diff < minDiff) {
        minDiff = diff;
        closest = label;
      }
    }
    return closest;
  };

  return {
    ...base,
    resumeSummary,
    finalRemarks: {
      status: finalRemarksParts.length > 0,
      data: finalRemarksParts.join("\n\n"),
    },
    score: {
      totalMarks: 100,
      scoredMarks: Math.round(avgPct),
      percentage: Math.round(avgPct),
    },
    overallFeedback: [
      {
        title: "CEFR Level",
        value: overallCefr ? cefrLevelToNumeric(overallCefr) / 100 : cefrLevelValue,
      },
      {
        title: "Resume Rating",
        value: interview?.feedback?.overAllRating?.resumeRating ?? 0,
      },
      { title: "Percentile", value: interview?.finalPercentileValue ?? 0 },
      { title: "Suspicious Activity", value: suspiciousActivity },
    ],
    cefrLevelLabel: (() => {
      if (overallCefr) return overallCefr;
      const target = Math.round(avgCefrNumeric);
      let closest = "A0",
        minDiff = Infinity;
      for (const [label, score] of Object.entries({
        A0: 0,
        A1: 15,
        A2: 30,
        "B1-": 40,
        B1: 50,
        "B1+": 55,
        "B2-": 60,
        B2: 70,
        "B2+": 75,
        "C1-": 80,
        C1: 85,
        "C1+": 90,
        "C2-": 95,
        C2: 100,
      })) {
        const diff = Math.abs(score - target);
        if (diff < minDiff) {
          minDiff = diff;
          closest = label;
        }
      }
      return closest;
    })(),
    overallScoreValue: overallScoreRaw !== null ? overallScoreNumeric : null,
    overallScoreLabel: overallScoreRaw !== null ? numericToCefrLabel(overallScoreNumeric) : null,
    totalParticipants: interview?.totalParticipants ?? 0,
    finalPercentile: interview?.finalPercentileValue ?? 0,
    questions: {
      status: Array.isArray(apiFeedback?.questions) && apiFeedback.questions.length > 0,
      data: apiFeedback?.questions ?? [],
    },
    proctoringLogs: {
      status: proctoringMessages.length > 0,
      messages: proctoringMessages,
      screenshots: (apiFeedback?.proctoring?.screenshots ?? []).map((s: any) =>
        s?.url ? { ...s, url: withSas(s.url, sas) } : s
      ),
      externalDevices: apiFeedback?.proctoring?.externalDevices ?? null,
    },
    screenshots: {
      status: Array.isArray(apiFeedback?.screenshots) && apiFeedback.screenshots.length > 0,
      images: (apiFeedback?.screenshots ?? []).map((u: string) => withSas(u, sas)),
    },
    deviceScreenshots: {
      status:
        Array.isArray(apiFeedback?.deviceScreenshots) && apiFeedback.deviceScreenshots.length > 0,
      images: (apiFeedback?.deviceScreenshots ?? []).map((u: string) => withSas(u, sas)),
    },
    AIGenTextBanner: interview?.AIGenTextBanner ?? false,
    AIGenText: interview?.AIGenText ?? false,
    cefrScores: {
      reading: activeSections.has("Reading") ? readingScores : base.cefrScores!.reading,
      listening: activeSections.has("Listening") ? listeningScores : base.cefrScores!.listening,
      writing: activeSections.has("Writing") ? writingScores : base.cefrScores!.writing,
      speaking: activeSections.has("Speaking") ? speakingScores : base.cefrScores!.speaking,
    },
    cefrScreeningSections: sections.length > 0 ? sections : base.cefrScreeningSections,
    // Store active sections for OverviewView to conditionally render panels
    cefrActiveSections: Array.from(activeSections),
    cefrDifficultyLevel: interview?.question?.difficultyLevel?.toLowerCase() ?? null,
    totalDuration: interview?.interviewDuration || 0,
  } as any;
};

/** Returns CEFR feedback: uses API data when available, otherwise base fallback */
const getCefrStaticFeedback = (apiFeedback?: any, interview?: any): FeedbackForm =>
  apiFeedback &&
  (apiFeedback.cefrQuestions?.length > 0 ||
    apiFeedback.readingSummary ||
    apiFeedback.listeningSummary ||
    apiFeedback.writingSummary ||
    apiFeedback.speakingSummary ||
    (apiFeedback.writing?.length ?? 0) > 0 ||
    (apiFeedback.speaking?.length ?? 0) > 0)
    ? transformCefrApiFeedbackToForm(apiFeedback, interview)
    : getCefrBaseFeedback();

// `feedbackStatus` lets an employer relabel/recolour the verdict pill (e.g. "Not Recommended" ->
// "Beta"). Match the interview status against the configured values and return the pill's label
// plus an inline style carrying only the colours the API actually sent, so anything missing falls
// back to the built-in label and Tailwind colours.
export const resolveFeedbackStatusChip = (
  status: string | undefined,
  feedbackStatus: FeedbackStatusOption[] | undefined
): { label: string; style?: CSSProperties } => {
  const fallback = { label: status ?? "" };
  if (!status || !Array.isArray(feedbackStatus)) return fallback;

  const normalized = status.trim().toLowerCase();
  const matched = feedbackStatus.find(
    (option) =>
      String(option?.value ?? "")
        .trim()
        .toLowerCase() === normalized
  );
  if (!matched) return fallback;

  const style: CSSProperties = {};
  if (matched.bgColor) style.backgroundColor = matched.bgColor;
  if (matched.textColor) style.color = matched.textColor;

  return {
    label: matched.label?.trim() || status,
    style: Object.keys(style).length ? style : undefined,
  };
};

// The rating-system legend spells out the same three verdicts as the header pill, so it has to
// honour the employer's `feedbackStatus` relabelling too. Falls back to the built-in verdict name
// when nothing is configured for it.
export const resolveFeedbackStatusLabel = (
  status: string,
  feedbackStatus: FeedbackStatusOption[] | undefined
): string => resolveFeedbackStatusChip(status, feedbackStatus).label || status;

// The three verdicts the rating-system bar is built from, low score to high.
const RATING_SCALE_VERDICTS = ["Not Recommended", "Cautiously Recommended", "Recommended"] as const;

// Swatches the platform ships with in `feedbackStatus`. A verdict still sitting on its default
// swatch means the employer never picked a colour for it, so the bar keeps its built-in look.
const DEFAULT_FEEDBACK_STATUS_BG: Record<string, string> = {
  "Not Recommended": "#F2DFDF",
  "Cautiously Recommended": "#F4EBDD",
  Recommended: "#DEECE3",
};

// Hex twins of the Tailwind stops the bar already uses (red-500 / yellow-400 / green-500). They
// only come into play once one verdict was recoloured and the whole bar has to move to an inline
// gradient — the untouched verdicts then keep exactly the colour they render with today.
const DEFAULT_RATING_SCALE_COLORS: Record<string, string> = {
  "Not Recommended": "#ef4444",
  "Cautiously Recommended": "#facc15",
  Recommended: "#22c55e",
};

const normalizeHex = (color: string | undefined): string =>
  String(color ?? "")
    .trim()
    .replace(/^#/, "")
    .toLowerCase();

// Returns the employer's colour for a verdict, or undefined when they left it on the default
// swatch (or sent nothing at all).
const customFeedbackStatusColor = (
  status: string,
  feedbackStatus: FeedbackStatusOption[] | undefined
): string | undefined => {
  if (!Array.isArray(feedbackStatus)) return undefined;

  const normalized = status.trim().toLowerCase();
  const matched = feedbackStatus.find(
    (option) =>
      String(option?.value ?? "")
        .trim()
        .toLowerCase() === normalized
  );
  const bgColor = matched?.bgColor?.trim();
  if (!bgColor) return undefined;

  return normalizeHex(bgColor) === normalizeHex(DEFAULT_FEEDBACK_STATUS_BG[status])
    ? undefined
    : bgColor;
};

// Colours for the rating-system scale. When all three verdicts still carry the default swatches
// the result is empty and the views render their hardcoded Tailwind gradient/dots untouched. As
// soon as one verdict is recoloured the bar switches to an inline gradient that pairs that new
// colour with the built-in colours of the two verdicts that were left alone.
export const resolveRatingScalePalette = (
  feedbackStatus: FeedbackStatusOption[] | undefined
): {
  barStyle?: CSSProperties;
  dotStyles: Record<string, CSSProperties | undefined>;
} => {
  const customColors = RATING_SCALE_VERDICTS.map((status) =>
    customFeedbackStatusColor(status, feedbackStatus)
  );

  const dotStyles: Record<string, CSSProperties | undefined> = {};
  RATING_SCALE_VERDICTS.forEach((status, index) => {
    const color = customColors[index];
    dotStyles[status] = color ? { backgroundColor: color } : undefined;
  });

  if (customColors.every((color) => !color)) return { dotStyles };

  const [low, medium, high] = RATING_SCALE_VERDICTS.map(
    (status, index) => customColors[index] ?? DEFAULT_RATING_SCALE_COLORS[status]
  );

  return {
    barStyle: {
      backgroundImage: `linear-gradient(to right, ${low} 0%, ${medium} 50%, ${high} 100%)`,
    },
    dotStyles,
  };
};

export const psychometricSkillWiseAverage = (feedback: any): number | null => {
  const rows =
    feedback?.psychometricSkillWise?.skill_results ??
    feedback?.psychometric_skill_wise?.skill_results;
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const scores = rows
    .map((row: any) => Number(row?.score))
    .filter((score: number) => Number.isFinite(score));
  if (scores.length === 0) return null;
  return scores.reduce((sum: number, score: number) => sum + score, 0) / scores.length;
};

export const normalizePsychometricScore = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  const raw = Number(value);
  if (!Number.isFinite(raw)) return null;
  return raw <= 10 ? raw * 10 : raw;
};

export const incbotFeedbackData = (interview: any): IncbotFeedbackDataResult => {
  const normalizeText = (text: string) => text.replace(/\s+/g, "").replace(/\r?\n|\r/g, "");

  const interviewType = interview?.interviewType ?? "default";

  // CEFR interview type: return profile from interview + dynamic CEFR feedback from API
  if (interviewType === "cefr_interview") {
    const determineStatus = (status: string | undefined, rating: number, data: any) => {
      if (
        status === "Recommended" ||
        status === "Cautiously Recommended" ||
        status === "Not Recommended"
      )
        return status;
      const low = data?.ratingSystem?.low || 5;
      const medium = data?.ratingSystem?.medium || 7;
      if (rating >= medium) return "Recommended";
      if (rating >= low) return "Cautiously Recommended";
      return "Not Recommended";
    };

    const sas = interview?.containerSasToken ?? null;

    const overallVerdict = interview?.feedback?.overallVerdict ?? interview?.overallVerdict;
    const verdictStatus = (() => {
      if (!overallVerdict)
        return determineStatus(interview?.status, interview?.finalRating ?? 0, interview);
      const v = overallVerdict.toLowerCase();
      if (v.includes("not")) return "Not Recommended";
      if (v.includes("cautious")) return "Cautiously Recommended";
      return "Recommended";
    })();

    const profile = {
      code: interview?.code,
      name: interview?.candidate?.name,
      email: interview?.candidate?.email,
      phone: `+${interview?.candidate?.phone}`,
      // [2026-09-17] ADDED — SEPARATE TICKET: carried through so ProctoringLogsView can name
      // who produced the feedback. Already in the API payload; nothing new is fetched.
      endCallStatus: interview?.endCallStatus,
      // The real server time the interview was closed — NOT the back-dated end ROW.
      interviewEndDate: interview?.interviewEndDate,
      interviewDate: interview?.interviewStartDate,
      timeZone: interview?.timeZone,
      timeOffset: interview?.timeOffset,
      position: interview?.position?.title,
      company: interview?.employer?.name ?? "InCruiter",
      status: verdictStatus,
      finalRating: interview?.finalRating ?? 0,
      logo: interview?.employer?.checkWhiteLabelledStatus?.data?.companyLogo || "",
      candidateImage: withSas(interview?.feedback?.candidateImage, sas),
      interviewDuration: interview?.interviewDuration,
      ratingSystem: interview?.ratingSystem ?? { low: 5, medium: 7 },
      deepfakeDetected: interview?.feedback?.deepFakeDetection,
      deepFakeAudioDetection: interview?.feedback?.deepFakeAudioDetection,
      deepFakeVideoDetection: interview?.feedback?.deepFakeVideoDetection,
      interviewType,
      interviewLanguage: interview?.interviewLanguage,
      feedbackStatus: interview?.feedbackStatus,
    };

    const permissionMap: Permissions = {
      showProctoringLogs: true,
      showPercentileRank: true,
      softSkills: {
        Fluency: true,
        Clarity: true,
        Grammar: true,
        Confidence: true,
      },
      finalRemarks: {
        Strengths: true,
        "Areas of Improvement": true,
        "Communication Skills": true,
        "Grammatical Correctness": true,
        Clarity: true,
        Conclusion: true,
      },
    };

    const hasCefrData = (obj: any) =>
      obj &&
      (obj.cefrQuestions?.length > 0 ||
        obj.readingSummary ||
        obj.listeningSummary ||
        obj.writingSummary ||
        obj.speakingSummary ||
        (obj.writing?.length ?? 0) > 0 ||
        (obj.speaking?.length ?? 0) > 0);
    const apiFeedback = hasCefrData(interview?.feedback)
      ? interview?.feedback
      : hasCefrData(interview)
        ? interview
        : undefined;
    return {
      profile,
      feedback: getCefrStaticFeedback(apiFeedback, interview),
      permissions: permissionMap,
    };
  }

  const sas = interview?.containerSasToken ?? null;

  // Profile Info
  const determineStatus = (status: string | undefined, rating: number, data: any) => {
    if (
      status === "Recommended" ||
      status === "Cautiously Recommended" ||
      status === "Not Recommended"
    ) {
      return status;
    }

    const low = data?.ratingSystem?.low || 5;
    const medium = data?.ratingSystem?.medium || 7;

    if (rating >= medium) return "Recommended";
    if (rating >= low) return "Cautiously Recommended";
    return "Not Recommended";
  };

  const psychometricOverallPercentage =
    interviewType === "psychometric_assessment"
      ? (normalizePsychometricScore(interview?.psychometricOverallScore) ??
        normalizePsychometricScore(psychometricSkillWiseAverage(interview?.feedback)))
      : null;
  const psychometricRating =
    psychometricOverallPercentage != null ? psychometricOverallPercentage / 10 : null;

  const profile = {
    code: interview?.code,
    name: interview?.candidate?.name,
    email: interview?.candidate?.email,
    phone: `+${interview?.candidate?.phone}`,
    // [2026-09-17] ADDED — SEPARATE TICKET: carried through so ProctoringLogsView can name
    // who produced the feedback. Already in the API payload; nothing new is fetched.
    endCallStatus: interview?.endCallStatus,
    // The real server time the interview was closed — NOT the back-dated end ROW.
    interviewEndDate: interview?.interviewEndDate,
    interviewDate: interview?.interviewStartDate,
    timeZone: interview?.timeZone,
    timeOffset: interview?.timeOffset,
    position: interview?.position?.title,
    company: interview?.employer?.name ?? "InCruiter",
    status:
      psychometricRating != null
        ? determineStatus(undefined, psychometricRating, interview)
        : determineStatus(interview?.status, interview?.finalRating ?? 0, interview),
    finalRating: psychometricRating ?? interview?.finalRating ?? 0,
    logo: interview?.employer?.checkWhiteLabelledStatus?.data?.companyLogo || "",
    candidateImage: withSas(interview?.feedback?.candidateImage, sas),
    interviewDuration: interview?.interviewDuration,
    ratingSystem: interview?.ratingSystem ?? { low: 5, medium: 7 },
    deepfakeDetected: interview?.feedback?.deepFakeDetection,
    deepFakeAudioDetection: interview?.feedback?.deepFakeAudioDetection,
    deepFakeVideoDetection: interview?.feedback?.deepFakeVideoDetection,
    interviewType: interview?.interviewType,
    interviewLanguage: interview?.interviewLanguage,
    feedbackStatus: interview?.feedbackStatus,
  };

  // Suspicious Activity Score
  const suspiciousRating = (() => {
    if (
      interview?.feedback?.overAllRating &&
      "suspiciousActivity" in interview.feedback.overAllRating
    ) {
      return interview.feedback.overAllRating.suspiciousActivity || 0;
    }

    const messages = interview?.feedback?.proctoring?.messages ?? [];
    const switchedTab = messages.some((m: any) =>
      m.text.includes("Candidate switched to another tab")
    );
    const lookedAway = messages.some((m: any) => m.text.includes("Looking away from screen"));
    return switchedTab ? 10 : lookedAway ? 5 : 0;
  })();

  // Question Parsing
  const rawQuestions = interview.questions?.length
    ? interview.questions
    : (interview.question?.questions ?? []);

  const finalQuestionList = rawQuestions
    .filter((q: any) => (q?.translate?.question || q?.question)?.trim())
    .map((q: any) => {
      const matched = interview?.feedback?.questions?.find((f: any) => {
        return normalizeText(q?.translate?.question || q?.question) === normalizeText(f.question);
      });

      if (matched) {
        if (q.codeBlock) matched.codeBlock = q.codeBlock;
        if (q.predefinedAnswer) matched.predefinedAnswer = q.predefinedAnswer;
        if (q.skill) matched.skill = q.skill;
        return matched;
      }

      return {
        question: q?.translate?.question || q?.question,
        answer: "The candidate didn't answer.",
        timeTaken: 0,
        rating: 0,
        questionType: "custom",
      };
    });

  // Written questions are defined on `question.questions` and answered in `feedback.questions`,
  // but `interview.questions` (instance list) may omit them or use different ordering — merge any
  // feedback rows with questionType `written` not already represented in finalQuestionList.
  const includedQuestionKeys = new Set(
    finalQuestionList.map((q: any) => normalizeText(q?.question || ""))
  );
  (interview?.feedback?.questions ?? []).forEach((fq: any) => {
    const qt = String(fq?.questionType || "").toLowerCase();
    if (qt !== "written") return;
    const key = normalizeText(fq?.question || "");
    if (!key || includedQuestionKeys.has(key)) return;
    finalQuestionList.push(fq);
    includedQuestionKeys.add(key);
  });

  // Total Duration (incl. AI Questions)
  // let totalDuration = parseInt(interview?.question?.totalDuration || 0);
  // interview?.feedback?.questions?.forEach((q: any) => {
  //   totalDuration += (q?.conversationAIQuestions?.length || 0) * 65;
  // });

  let totalDuration = interview.interviewDuration || 0;

  // Resume Summary Cleanup
  const cleanResumeSummary =
    interview?.feedback?.resumeSummary
      ?.replace(/\\r/g, "")
      ?.replace(/\\n/g, "")
      ?.replace(/\\t/g, "&nbsp;&nbsp;&nbsp;&nbsp;")
      ?.replace(/\\s/g, "&nbsp;")
      ?.replace("<br /><br />", "") ?? "";

  // Settings Parsing (for state use)
  const settingsPermissions = interview?.employer?.userSettings?.feedbackReportPermission ?? [];

  const permissionMap = settingsPermissions.reduce(
    (acc: any, item: any) => {
      if (item.key === "proctoring_log") acc.showProctoringLogs = item.status;

      if (item.key === "percentile_rank") acc.showPercentileRank = item.status;

      if (item.key === "soft_skill_assessment") {
        acc.softSkills = item.resources.reduce((obj: any, r: any) => {
          obj[r.name] = r.status;
          return obj;
        }, {});
      }

      if (item.key === "final_remark") {
        acc.finalRemarks = item.resources.reduce((obj: any, r: any) => {
          obj[r.name] = r.status;
          return obj;
        }, {});
      }

      return acc;
    },
    {
      showProctoringLogs: true,
      showPercentileRank: true,
      softSkills: {
        Fluency: true,
        Clarity: true,
        Grammar: true,
        Confidence: true,
      },
      finalRemarks: {
        Strengths: true,
        "Areas of Improvement": true,
        "Communication Skills": true,
        "Grammatical Correctness": true,
        Clarity: true,
        Conclusion: true,
      },
    }
  );

  // Process aptitude section data
  const processAptitudeSectionData = () => {
    const aptitude = [
      ...interview?.question?.aptitude,
      ...(interview?.question?.technicalMcqs || []),
    ];
    const questions = interview?.questions || [];
    const questionsFeedback = interview?.feedback?.questions || [];

    // Enrich questions with feedback data
    const enrichedQuestions = [...questions].map((q) => {
      const match = questionsFeedback.find(
        (fq: any) => normalizeText(fq.question) === normalizeText(q.question)
      );

      if (match) {
        return {
          ...q,
          rating: match.rating,
          answer: match.answer,
          questionSubmitTimestamp: match.questionSubmitTimestamp,
        };
      }
      return q;
    });

    // Process aptitude data
    if (!aptitude) return { sectionsData: [], totalAptitudeTime: 0 };

    const sectionsData = aptitude.map((apt: any) => {
      const matchedQuestions = enrichedQuestions.filter(
        (q: any) =>
          q?.questionType === apt?.questionType &&
          q?.difficultyLevel?.toLowerCase() === apt?.difficultyLevel?.toLowerCase()
      );

      const totalMarks = matchedQuestions.reduce(
        (acc: number, q: any) => (q.rating ? acc + q.rating : acc),
        0
      );

      const lastQuestionTime =
        matchedQuestions.length > 0
          ? Math.max(...matchedQuestions.map((q: any) => q.questionSubmitTimestamp || 0))
          : 0;

      return {
        ...apt,
        totalMarks,
        lastQuestionTime,
        responses: matchedQuestions,
      };
    });

    // Calculate total aptitude time
    const totalAptitudeTime = sectionsData.reduce(
      (acc: number, apt: any) => acc + (apt.lastQuestionTime || 0),
      0
    );

    return {
      sectionsData,
      totalAptitudeTime,
    };
  };

  const aptitudeSectionData = processAptitudeSectionData();

  const buildPsychometricSkillsScores = (): {
    title: string;
    value: number;
  }[] => {
    const skillWiseRaw =
      interview?.feedback?.psychometricSkillWise ?? interview?.feedback?.psychometric_skill_wise;
    const skillResults = skillWiseRaw?.skill_results;

    if (Array.isArray(skillResults) && skillResults.length > 0) {
      return skillResults.map((row: { skill?: string; score?: unknown }, idx: number) => {
        const titleRaw = String(row?.skill ?? "").trim();
        const title = titleRaw || `Skill ${idx + 1}`;
        const n = Number(row?.score);
        const value = Number.isFinite(n) ? Math.round(n) : 0;
        return { title, value };
      });
    }

    const ia = interview?.feedback?.interviewAssessment as Record<string, unknown> | undefined;

    const num = (v: unknown) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };

    if (!ia) return [];

    const rows: { title: string; value: number }[] = [
      { title: "Communication Skills", value: num(ia.communicationSkills) },
      {
        title: "Emotional Intelligence",
        value: num(ia.emotionalIntelligence ?? ia.emotional_intelligence),
      },
      {
        title: "Stress & Resilience",
        value: num(ia.stressAndResilience ?? ia.stress_resilience ?? ia.stressResilience),
      },
      {
        title: "Teamwork Skills",
        value: num(ia.teamworkSkills ?? ia.teamwork_skills),
      },
      {
        title: "Decision-Making",
        value: num(ia.decisionMaking ?? ia.decision_making),
      },
      { title: "Creativity", value: num(ia.creativity) },
      {
        title: "Strategic Thinking",
        value: num(ia.strategicThinking ?? ia.strategic_thinking),
      },
      { title: "Adaptability", value: num(ia.adaptability) },
    ];

    return rows.some((r) => r.value > 0) ? rows : [];
  };

  // Feedback Object
  const feedback = {
    isSkillBased: interview?.question?.evaluationType === "skill_based" ? true : false,
    feedbackTemplate:
      interview && "feedbackTemplate" in interview ? interview?.feedbackTemplate : true,
    duration: interview?.totalDuration,
    totalDuration,
    codingStats: interview?.codingQuestionStats,
    positionStats: interview?.positionSpecificQuestionStats,
    writtenStats: interview?.writtenQuestionStats,
    aptitudeStats: interview?.aptitudeSpecificQuestionStats,
    aptitudeSectionData,
    finalPercentile: interview?.finalPercentileValue,
    showPercentileRank: permissionMap.showPercentileRank !== false,
    psychometricOverallScore:
      interview?.interviewType === "psychometric_assessment"
        ? (interview?.psychometricOverallScore ??
          psychometricSkillWiseAverage(interview?.feedback) ??
          null)
        : null,
    psychometricPercentile:
      interview?.interviewType === "psychometric_assessment"
        ? (interview?.psychometricPercentile ?? null)
        : null,
    totalTimeTaken: interview?.totalTimeTaken,
    totalParticipants: interview?.totalParticipants,
    version: interview?.feedback?.feedbackVersion || 1,
    resumeSummary: cleanResumeSummary,
    questions: {
      status: finalQuestionList.length > 0,
      data: finalQuestionList,
    },
    proctoringLogs: {
      status: !!interview?.feedback?.proctoring && permissionMap.showProctoringLogs !== false,
      messages: interview?.feedback?.proctoring?.messages ?? [],
      screenshots: interview?.feedback?.proctoring?.screenshots ?? [],
      externalDevices: interview?.feedback?.proctoring?.externalDevices ?? null,
    },
    screenshots: {
      status: (interview?.feedback?.screenshots?.length ?? 0) > 0,
      images: (interview?.feedback?.screenshots ?? []).map((u: string) => withSas(u, sas)),
    },
    deviceScreenshots: {
      status: (interview?.feedback?.deviceScreenshots?.length ?? 0) > 0,
      images: (interview?.feedback?.deviceScreenshots ?? []).map((u: string) => withSas(u, sas)),
    },
    finalRemarks: {
      status: !!interview?.feedback?.remark,
      data:
        interview?.feedback?.remark ??
        "Unfortunately, we couldn’t generate final remarks as not all the questions were answered.",
    },
    score: interview?.feedback?.overAllRating?.score ?? null,
    overallFeedback: [
      {
        title: interview?.feedback?.feedbackVersion > 3 ? "Answer Accuracy" : "Interview Score",
        value: interview?.feedback?.overAllRating?.interviewScore,
      },
      {
        title: "Resume Rating",
        value: interview?.feedback?.overAllRating?.resumeRating,
      },
      {
        title: "Suspicious Activity",
        value: suspiciousRating,
      },
    ],
    softSkillAssessment: [
      {
        title: "Fluency",
        value: interview?.feedback?.interviewAssessment?.fluency ?? 0,
        status: permissionMap.softSkills["Fluency"] ?? true,
      },
      {
        title: "Clarity",
        value: interview?.feedback?.interviewAssessment?.clarity ?? 0,
        status: permissionMap.softSkills["Clarity"] ?? true,
      },
      {
        title: "Grammar",
        value: interview?.feedback?.interviewAssessment?.grammar ?? 0,
        status: permissionMap.softSkills["Grammar"] ?? true,
      },
      {
        title: "Confidence",
        value: interview?.feedback?.interviewAssessment?.confidence ?? 0,
        status: permissionMap.softSkills["Confidence"] ?? true,
      },
    ],
    interviewAssessment: [
      {
        title: "Communication Skills",
        value: interview?.feedback?.interviewAssessment?.communicationSkills ?? 0,
      },
      {
        title: "Fluency",
        value: interview?.feedback?.interviewAssessment?.fluency ?? 0,
      },
      {
        title: "Confidence",
        value: interview?.feedback?.interviewAssessment?.confidence ?? 0,
      },
      {
        title: "Answer Accuracy",
        value: interview?.feedback?.interviewAssessment?.accuracy ?? 0,
      },
    ],
    psychometricSkillsScores: buildPsychometricSkillsScores(),
    psychometricSkillWise:
      interview?.feedback?.psychometricSkillWise ??
      interview?.feedback?.psychometric_skill_wise ??
      null,
    psychometricOverallReport:
      interview?.feedback?.psychometricOverallReport ??
      interview?.feedback?.psychometric_overall_report ??
      null,
    psychometricQuestionsFeedback: (
      interview?.feedback?.psychometricQuestions ??
      interview?.feedback?.psychometric_questions ??
      []
    ).map((q: any) => ({
      ...q,
      answerAudioUrl: q?.answerAudioUrl ? withSas(q.answerAudioUrl, sas) : null,
    })),
    psychometricQuestionBank: Array.isArray(interview?.psychometricQuestions)
      ? interview.psychometricQuestions
      : [],
    AIGenTextBanner: interview?.AIGenTextBanner || false,
    AIGenText: interview?.AIGenText || false,
    userSettings: interview?.employer?.userSettings ?? null,
    codingAndAptitude: interview?.question?.codingAndAptitude || false,
    codingFinalRemark: interview?.coding_final_remark || [],
  };

  return {
    profile,
    feedback,
    permissions: permissionMap,
    psychometricSkills: interview?.question?.psychometricSkills ?? [],
  };
};

export const formatDate = (dateString: string, type: number): string => {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();
  const weekday = date.toLocaleString("en-US", { weekday: "short" });

  if (type === 1) return `${month} ${day}, ${year}`;
  return `${weekday}, ${month} ${day}, ${year}`;
};

export const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  let result = "";

  if (hours > 0) {
    result += `${hours} ${hours === 1 ? "hr" : "hrs"}`;
  }

  if (minutes > 0 || (hours > 0 && secs === 0)) {
    if (result) result += " ";
    result += `${minutes} ${minutes === 1 ? "min" : "mins"}`;
  }

  if ((secs > 0 && hours === 0) || (secs > 0 && minutes === 0)) {
    if (result) result += " ";
    result += `${secs} ${secs === 1 ? "sec" : "secs"}`;
  }

  return result || "0 secs";
};

export const getStatusV = (skillRating: number) => {
  const score = parseInt(String(skillRating));
  if (score <= 2) return { title: "Very Poor", color: "red" };
  if (score <= 4) return { title: "Poor", color: "red" };
  if (score === 5) return { title: "Average", color: "yellow" };
  if (score === 6) return { title: "Above Average", color: "yellow" };
  if (score === 7) return { title: "Good", color: "blue" };
  if (score === 8) return { title: "Very Good", color: "green" };
  if (score >= 9) return { title: "Excellent", color: "green" };
  return { title: "Very Poor", color: "red" }; // fallback
};

export const capitalizeAll = (s: string) => {
  if (typeof s !== "string") return "";
  return s
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};
